/** Four core web reports approved 13 October 2026; broader typed capabilities are deferred. */
import * as z from 'zod';
export const GSC_REPORT_MATRIX_VERSION = '2.0.0';
export const GSC_REPORT_CAPTURE_OPERATION_ID = 'gsc.search_analytics.capture_report';
export const GSC_REPORT_CAPTURE_OPERATION_VERSION = '1.0.0';
export const GSC_AVAILABLE_DATES_OPERATION_ID = 'gsc.search_analytics.available_dates';
export const GSC_WAREHOUSE_OPERATION_IDS = [GSC_REPORT_CAPTURE_OPERATION_ID, GSC_AVAILABLE_DATES_OPERATION_ID];
export const GSC_CAPTURE_ROW_LIMIT = 25000;
export const GSC_SOURCE_DAILY_ROW_LIMIT = 50000;
export const gscSearchTypeSchema = z.enum(['web', 'image', 'video', 'news', 'discover', 'googleNews']);
export const gscReportFamilySchema = z.enum([
    'total',
    'geography',
    'page',
    'query',
    'page_query',
    'appearance',
    'appearance_detail',
]);
const sourceDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u)
    .refine((value) => {
    const date = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}, 'Invalid Pacific calendar date');
export const gscReportInputSchema = z
    .strictObject({
    sourceDate: sourceDateSchema,
    searchType: gscSearchTypeSchema,
    report: gscReportFamilySchema,
    appearance: z.string().min(1).max(4096).optional(),
})
    .superRefine((input, ctx) => {
    if ((input.report === 'appearance_detail') !== (input.appearance !== undefined))
        ctx.addIssue({
            code: 'custom',
            message: 'Appearance is required only for appearance detail',
        });
    if (isNewsFeed(input.searchType) && ['query', 'page_query'].includes(input.report))
        ctx.addIssue({
            code: 'custom',
            message: 'Query reports are unsupported for Discover/Google News',
        });
});
export const gscAvailabilityInputSchema = z
    .strictObject({
    from: sourceDateSchema,
    to: sourceDateSchema,
})
    .refine((input) => {
    const days = (Date.parse(input.to) - Date.parse(input.from)) / 86400000;
    return days >= 0 && days <= 90;
}, 'Invalid availability window');
export function buildGscAvailabilityRequest(value, type) {
    const input = gscAvailabilityInputSchema.parse(value);
    return {
        startDate: input.from,
        endDate: input.to,
        type: gscSearchTypeSchema.parse(type),
        dimensions: ['date'],
        aggregationType: 'auto',
        rowLimit: GSC_CAPTURE_ROW_LIMIT,
        startRow: 0,
        dataState: 'final',
    };
}
function isNewsFeed(type) {
    return type === 'discover' || type === 'googleNews';
}
export const GSC_BASE_REPORTS = [
    { searchType: 'web', report: 'total' },
    { searchType: 'web', report: 'query' },
    { searchType: 'web', report: 'page' },
    { searchType: 'web', report: 'page_query' },
];
export const GSC_COLLECTION_SEARCH_TYPES = [...new Set(GSC_BASE_REPORTS.map((recipe) => recipe.searchType))];
export const GSC_COLLECTION_SCOPE = {
    matrixVersion: GSC_REPORT_MATRIX_VERSION,
    searchTypes: GSC_COLLECTION_SEARCH_TYPES,
    reports: GSC_BASE_REPORTS.map((recipe) => recipe.report),
};
/** Dates are provider calendar labels. Never floor a UTC instant into a purported Pacific day. */
export function buildGscDailyReportRequest(value, startRow) {
    const input = gscReportInputSchema.parse(value);
    if (!Number.isSafeInteger(startRow) ||
        startRow < 0 ||
        startRow >= GSC_SOURCE_DAILY_ROW_LIMIT ||
        startRow % GSC_CAPTURE_ROW_LIMIT)
        throw new Error('GSC_CAPTURE_INPUT_INVALID: invalid page offset');
    const geography = isNewsFeed(input.searchType) ? ['country'] : ['country', 'device'];
    const detailGeography = input.searchType === 'web' ? [] : geography;
    const dimensions = (() => {
        switch (input.report) {
            case 'total':
                return [];
            case 'geography':
                return geography;
            case 'page':
                return ['page', ...detailGeography];
            case 'query':
                return ['query', ...detailGeography];
            case 'page_query':
                return ['page', 'query', ...detailGeography];
            case 'appearance':
                return ['searchAppearance'];
            case 'appearance_detail':
                return isNewsFeed(input.searchType) ? ['page', ...geography] : ['page', 'query', ...geography];
        }
    })();
    const aggregationType = !isNewsFeed(input.searchType) && ['total', 'geography', 'query'].includes(input.report)
        ? 'byProperty'
        : 'auto';
    return {
        startDate: input.sourceDate,
        endDate: input.sourceDate,
        type: input.searchType,
        dimensions,
        aggregationType,
        rowLimit: GSC_CAPTURE_ROW_LIMIT,
        startRow,
        dataState: 'final',
        ...(input.appearance === undefined
            ? {}
            : {
                dimensionFilterGroups: [
                    {
                        groupType: 'and',
                        filters: [
                            {
                                dimension: 'searchAppearance',
                                operator: 'equals',
                                expression: input.appearance,
                            },
                        ],
                    },
                ],
            }),
    };
}
//# sourceMappingURL=gsc-report-recipes.js.map